import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Eye, EyeOff, ArrowLeft, ArrowRight, User, Mail, Lock, ShieldCheck } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { useLang } from "@/i18n";

const INPUT_CLS =
  "h-[52px] rounded-lg bg-[#111B28] border-[#354458] text-[#F8FAFC] placeholder:text-[#94A3B8] focus-visible:border-[#5B739B] focus-visible:ring-2 focus-visible:ring-[#3B5588]/50";

export default function Register() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const { tf, lang, setLang } = useLang();
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", password: "", confirm: "",
  });
  const [showPwd, setShowPwd] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState({});

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const validate = () => {
    const e = {};
    if (!form.first_name.trim()) e.first_name = tf("Prénom requis");
    if (!form.last_name.trim()) e.last_name = tf("Nom requis");
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim())) e.email = tf("Email invalide");
    if (form.password.length < 8) e.password = tf("8 caractères minimum");
    if (form.password !== form.confirm) e.confirm = tf("Les mots de passe ne correspondent pas");
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (busy || !validate()) return;
    setBusy(true);
    try {
      const r = await api.post("/auth/register", {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        email: form.email.trim().toLowerCase(),
        password: form.password,
      });
      setUser(r.data);
      toast.success(tf("Compte créé — bienvenue !"));
      const inviteToken = (() => { try { return localStorage.getItem("athlynx_invite_token"); } catch { return null; } })();
      navigate(inviteToken ? "/join" : "/dashboard", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || tf("Création impossible"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen w-full bg-[#0A0C12] flex" data-testid="register-page">
      {/* === LEFT PANEL — brand & promise === */}
      <aside
        className={cn(
          "hidden md:flex relative flex-col justify-between",
          "w-[44%] lg:w-[48%] xl:w-[44%] p-10 lg:p-14 overflow-hidden"
        )}
        aria-hidden="false"
      >
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse at 20% 0%, #B91D14 0%, #6E1209 35%, #1B0606 70%, #0A0C12 100%)",
          }}
          aria-hidden
        />
        <div
          className="absolute inset-0 mix-blend-overlay opacity-60"
          style={{
            background:
              "radial-gradient(circle at 75% 80%, rgba(255,140,40,0.5), transparent 55%)",
          }}
          aria-hidden
        />
        <div
          className="absolute inset-0 opacity-[0.05]"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160' viewBox='0 0 160 160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%25' height='100%25' filter='url(%23n)' opacity='0.5'/></svg>\")",
          }}
          aria-hidden
        />

        <header className="relative z-10 flex items-center gap-3">
          <img
            src="/athlynx-logo-white.svg"
            alt="Athlynx"
            className="h-9 w-9 select-none"
            draggable={false}
          />
          <div className="text-white/95 font-semibold tracking-tight text-lg">Athlynx</div>
        </header>

        <div className="relative z-10 max-w-md space-y-5">
          <div className="text-[10px] uppercase tracking-[0.22em] text-white/60 font-semibold">
            {tf("Plateforme de gestion sportive")}
          </div>
          <h1 className="text-3xl lg:text-4xl xl:text-5xl font-semibold text-white leading-[1.05] tracking-tight">
            {tf("Votre réseau.")}
            <br />
            {tf("Vos athlètes.")}
            <br />
            <span className="text-white/70">{tf("Votre temps.")}</span>
          </h1>
          <p className="text-white/70 text-[15px] leading-relaxed max-w-prose">
            {tf("Athlynx centralise vos opportunités, vos échanges et la gestion de vos athlètes. Pipeline, calendrier, relations et dossiers réunis dans un seul outil.")}
          </p>
        </div>

        <footer className="relative z-10 flex items-center gap-2 text-[12px] text-white/55">
          <ShieldCheck className="h-3.5 w-3.5" />
          {tf("Sécurisé. Fiable. Conçu pour les agents.")}
        </footer>
      </aside>

      {/* === RIGHT PANEL — registration form === */}
      <main className="relative flex-1 flex flex-col items-center justify-center px-5 py-10 md:px-10 bg-[#080D15]">
        <div
          role="group"
          aria-label={tf("Choisir la langue")}
          data-testid="register-lang-switch"
          className="absolute top-4 right-4 md:top-5 md:right-5 z-20 inline-flex items-center rounded-lg border border-white/[0.12] bg-white/[0.04] p-0.5"
        >
          {["fr", "en"].map((l) => (
            <button
              key={l}
              type="button"
              onClick={() => setLang(l)}
              aria-pressed={lang === l}
              data-testid={`register-lang-${l}`}
              className={
                lang === l
                  ? "h-8 min-w-[38px] px-2.5 rounded-md bg-[#F5F7FA] text-[#0A0C12] text-xs font-semibold tracking-wide transition-colors"
                  : "h-8 min-w-[38px] px-2.5 rounded-md text-[#8E9CB2] hover:text-white text-xs font-medium tracking-wide transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-white/40"
              }
            >
              {l.toUpperCase()}
            </button>
          ))}
        </div>

        <div className="w-full max-w-[520px]">
          <Link
            to="/login"
            className="inline-flex items-center gap-1.5 text-xs text-[#93A5C0] hover:text-white transition-colors mb-6"
            data-testid="register-back-link"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            {tf("Retour")}
          </Link>

          <div className="flex items-center gap-2 mb-6">
            <img src="/athlynx-logo-white.svg" alt="Athlynx" className="h-7 w-7" />
            <span className="text-base font-semibold tracking-tight text-white">Athlynx</span>
          </div>

          <header className="mb-7 space-y-1">
            <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-[#F5F7FA]">
              {tf("Créer un compte")}
            </h2>
            <p className="text-sm text-[#9AA9BF]">
              {tf("Rejoignez Athlynx en quelques secondes.")}
            </p>
          </header>

          <form onSubmit={handleSubmit} className="space-y-4" data-testid="register-form" noValidate>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field id="reg-firstname" label={tf("Prénom")} error={errors.first_name}>
                <div className="relative">
                  <User className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[#7C8CA5]" aria-hidden />
                  <Input
                    id="reg-firstname"
                    value={form.first_name}
                    onChange={update("first_name")}
                    autoComplete="given-name"
                    required
                    placeholder={tf("Ex. John")}
                    className={cn(INPUT_CLS, "pl-10")}
                    aria-invalid={!!errors.first_name}
                    data-testid="register-firstname"
                  />
                </div>
              </Field>
              <Field id="reg-lastname" label={tf("Nom")} error={errors.last_name}>
                <div className="relative">
                  <User className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[#7C8CA5]" aria-hidden />
                  <Input
                    id="reg-lastname"
                    value={form.last_name}
                    onChange={update("last_name")}
                    autoComplete="family-name"
                    required
                    placeholder={tf("Ex. Smith")}
                    className={cn(INPUT_CLS, "pl-10")}
                    aria-invalid={!!errors.last_name}
                    data-testid="register-lastname"
                  />
                </div>
              </Field>
            </div>

            <Field id="reg-email" label={tf("Email")} error={errors.email}>
              <div className="relative">
                <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[#7C8CA5]" aria-hidden />
                <Input
                  id="reg-email"
                  type="email"
                  value={form.email}
                  onChange={update("email")}
                  autoComplete="email"
                  required
                  placeholder={tf("votre@email.com")}
                  className={cn(INPUT_CLS, "pl-10")}
                  aria-invalid={!!errors.email}
                  data-testid="register-email"
                />
              </div>
            </Field>

            <Field id="reg-password" label={tf("Mot de passe")} error={errors.password} hint={tf("8 caractères minimum")}>
              <div className="relative">
                <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[#7C8CA5]" aria-hidden />
                <Input
                  id="reg-password"
                  type={showPwd ? "text" : "password"}
                  value={form.password}
                  onChange={update("password")}
                  autoComplete="new-password"
                  required
                  minLength={8}
                  placeholder={tf("Créez un mot de passe")}
                  className={cn(INPUT_CLS, "pl-10 pr-11")}
                  aria-invalid={!!errors.password}
                  data-testid="register-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPwd((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 h-9 w-9 rounded-md flex items-center justify-center text-[#7C8CA5] hover:text-white transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-white/40"
                  aria-label={showPwd ? tf("Cacher") : tf("Afficher")}
                  data-testid="register-toggle-password"
                >
                  {showPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </Field>

            <Field id="reg-confirm" label={tf("Confirmer le mot de passe")} error={errors.confirm}>
              <div className="relative">
                <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[#7C8CA5]" aria-hidden />
                <Input
                  id="reg-confirm"
                  type={showConfirm ? "text" : "password"}
                  value={form.confirm}
                  onChange={update("confirm")}
                  autoComplete="new-password"
                  required
                  minLength={8}
                  placeholder={tf("Confirmez votre mot de passe")}
                  className={cn(INPUT_CLS, "pl-10 pr-11")}
                  aria-invalid={!!errors.confirm}
                  data-testid="register-confirm"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirm((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 h-9 w-9 rounded-md flex items-center justify-center text-[#7C8CA5] hover:text-white transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-white/40"
                  aria-label={showConfirm ? tf("Cacher") : tf("Afficher")}
                  data-testid="register-toggle-confirm"
                >
                  {showConfirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </Field>

            <button
              type="submit"
              disabled={busy}
              className={cn(
                "w-full h-[54px] rounded-lg inline-flex items-center justify-center gap-2 mt-1",
                "bg-[#2458D6] text-white font-semibold text-[15px]",
                "hover:bg-[#1E4BBE] active:scale-[0.99] transition-all duration-150",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40 focus-visible:ring-offset-2 focus-visible:ring-offset-[#080D15]",
                "disabled:opacity-60 disabled:cursor-not-allowed"
              )}
              data-testid="register-submit"
            >
              {busy ? tf("Création…") : (
                <>
                  {tf("Créer mon compte")}
                  <ArrowRight className="h-4 w-4" />
                </>
              )}
            </button>
          </form>

          <div className="mt-5 text-center text-[13px] text-[#93A5C0]">
            {tf("Déjà un compte ?")}{" "}
            <Link to="/login" className="text-white font-medium hover:underline" data-testid="register-login-link">
              {tf("Se connecter")}
            </Link>
          </div>

          <p className="mt-7 text-[11px] text-center text-[#8B9BB4] leading-relaxed" data-testid="register-legal">
            {tf("En créant un compte, vous acceptez nos conditions d'utilisation et notre politique de confidentialité.")}
          </p>
        </div>
      </main>
    </div>
  );
}

function Field({ id, label, error, hint, children }) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} className="text-xs text-[#E5E7EB]">{label}</Label>
      {children}
      {error ? (
        <p className="text-[11px] text-red-400" role="alert">{error}</p>
      ) : hint ? (
        <p className="text-[10px] text-[#8B9BB4]">{hint}</p>
      ) : null}
    </div>
  );
}
